import SteppedProgressBar from '@rio-cloud/rio-uikit/SteppedProgressBar';
import { useMemo } from 'react';

import type { CardOrdersResponseType } from '@/api/schema';
import ShippingLabel from '@/components/steps/ShippingAddressStep/ShippingLabel';
import CardTable from '@/features/chargingCards/history/CardTable';
import { OrderStatus } from '@/features/chargingCards/history/OrderBody';
import { useDisplayNames } from '@/hooks/useDisplayNames';
import { t } from '@/lib/utils';

export type OrderBodyDetailsProps = {
    order: CardOrdersResponseType;
    locale: string;
};

const OrderBodyDetails = ({ order, locale }: OrderBodyDetailsProps) => {
    const formatter = useDisplayNames();

    const currentStatus = OrderStatus[order.cardStatus as keyof typeof OrderStatus];

    const timelineLabels = useMemo(
        () => [
            {
                icon: <i className={`uikit-icon ${OrderStatus.RECEIVED.icon}`} />,
                label: (
                    <div className='display-flex flex-column text-size-14'>
                        <span className='margin-x-auto max-width-60pct'>{t(OrderStatus.RECEIVED.value)}</span>
                        <span className='text-color-dark'>{new Date(order.orderDate).toLocaleDateString(locale)}</span>
                    </div>
                ),
                labelClassName: 'overflow-visible',
            },
            {
                icon: <i className={`uikit-icon ${OrderStatus.PLACED.icon} text-size-20`} />,
                label: <span className='text-size-14'>{t(OrderStatus.PLACED.value)}</span>,
            },
            {
                icon: <i className={`uikit-icon ${OrderStatus.SHIPPED.icon} text-size-20`} />,
                label: (
                    <div className='display-flex flex-column text-size-14'>
                        <span className='margin-x-auto max-width-60pct'>{t(OrderStatus.SHIPPED.value)}</span>
                        {order.shippingDate && (
                            <span className='text-color-dark'>
                                {new Date(order.shippingDate).toLocaleDateString(locale)}
                            </span>
                        )}
                    </div>
                ),
                labelClassName: 'overflow-visible',
            },
        ],
        [order.orderDate, order.shippingDate, locale]
    );

    return (
        <>
            <span className='text-color-dark'>{order.user?.email}</span>
            <div className='display-flex justify-content-between margin-top-25'>
                <div className='display-flex flex-basis-60pct'>
                    <div className='display-flex flex-column gap-2 width-80pct'>
                        <span className='text-medium'>{t('orderHistory.orderTimeline')}</span>
                        <SteppedProgressBar
                            variant='circle'
                            selectedStepNumber={currentStatus.id}
                            onSelectedChanged={() => {}}
                            className='margin-top-15'
                            labels={timelineLabels}
                        />
                    </div>
                </div>
                <div className='display-flex flex-basis-40pct'>
                    <div className='display-flex flex-column gap-2 flex-1-1-0'>
                        <span className='text-medium'>{t('general.shippingAddress')}</span>
                        <ShippingLabel
                            companyName={order.address.addressee}
                            recipient={order.address.instructions}
                            addressLine1={order.address.firstAddressLine}
                            addressLine2={order.address.secondAddressLine}
                            postCode={order.address.zip}
                            city={order.address.city}
                            countryLabel={formatter?.(order.address.countryCode)}
                        />
                    </div>
                </div>
                <div className='flex-basis-5pct' />
            </div>
            <div className='display-flex flex-column gap-10 margin-top-15'>
                <span className='text-medium'>{`${t('orderHistory.cardsOrdered')} (${order.cards.length})`}</span>
                <CardTable cards={order.cards} />
            </div>
        </>
    );
};

export default OrderBodyDetails;
